import type { ReactNode } from 'react';
import { ArrowDown, ArrowUp, Rows2, Rows3, X } from 'lucide-react';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import type { ListSort } from '@/hooks/list-params-serde';
import { DataTableColumnsMenu, type ColumnEntry } from './data-table-columns-menu';
import type { DataTableDensity } from './types';

export interface DataTableToolbarProps {
  children?: ReactNode;
  rowCount: number;
  totalCount: number;
  /** The view counts the rows itself, with the totals the server reported. */
  hideRowCount?: boolean;
  density: DataTableDensity;
  onDensityChange: (density: DataTableDensity) => void;
  columns: readonly ColumnEntry[];
  onToggleColumn: (id: string, visible: boolean) => void;
  onMoveColumn: (id: string, direction: -1 | 1) => void;
  onResetColumns: () => void;
  /** Current order, spelled out where the header that carries it may be off-screen. */
  sort?: ListSort | null;
  onSortChange?: (sort: ListSort | null) => void;
  /**
   * Cómo viene ordenada la lista cuando nadie eligió nada.
   *
   * Sin esto, quitar el orden **borraba el indicador entero**: la lista seguía
   * teniendo un orden —el del servidor— y la pantalla dejaba de decir cuál, que
   * es justo lo que el indicador existe para evitar. Ahora la ficha se queda,
   * dice el orden de verdad, y pierde la x porque ya no hay nada que quitar.
   */
  defaultSortLabel?: string;
  /**
   * Esconde el par normales/compactas.
   *
   * La diferencia es de doce píxeles de alto de fila y en una tabla con celdas
   * que ya traen su propio espaciado no se distingue: el control existe, hace
   * algo, y nadie puede ver qué. Donde no se note, sobra.
   */
  hideDensity?: boolean;
  columnLabels?: Record<string, string>;
  className?: string;
}

const DENSITY_LABEL: Record<DataTableDensity, string> = {
  compact: 'Filas compactas',
  default: 'Filas normales',
};

/**
 * The order of the list, said in words.
 *
 * The only other sign of it is an arrow in a column header, and in a table
 * wider than its panel that header is routinely off-screen — the list then
 * looks shuffled for no reason. The chip also gives the order somewhere to be
 * removed from, which a header that cannot be reached does not.
 */
function SortChip({
  sort,
  label,
  onClear,
}: {
  sort: ListSort | null;
  label: string;
  onClear?: () => void;
}) {
  const Arrow = sort?.dir === 'asc' ? ArrowUp : ArrowDown;
  const direction = sort?.dir === 'asc' ? 'ascendente' : 'descendente';

  return (
    <span className="inline-flex h-7 shrink-0 items-center gap-1 rounded-md border border-border bg-muted/60 pl-2 pr-1 text-xs">
      <span className="text-muted-foreground">Orden:</span>
      <span className="max-w-40 truncate font-medium">{label}</span>
      <Arrow className="size-3 shrink-0 text-muted-foreground" aria-hidden />
      <span className="sr-only">{direction}</span>
      {onClear && (
        <button
          type="button"
          aria-label={`Quitar el orden por ${label}`}
          onClick={onClear}
          className="grid size-4 shrink-0 place-items-center rounded-sm text-muted-foreground transition-colors duration-150 ease-out hover:bg-background hover:text-foreground"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

export function DataTableToolbar({
  children,
  rowCount,
  totalCount,
  hideRowCount = false,
  density,
  onDensityChange,
  hideDensity = false,
  columns,
  onToggleColumn,
  onMoveColumn,
  onResetColumns,
  sort,
  onSortChange,
  defaultSortLabel,
  columnLabels,
  className,
}: DataTableToolbarProps) {
  const sortLabel = sort
    ? (columnLabels?.[sort.id] ?? columns.find((column) => column.id === sort.id)?.label ?? sort.id)
    : null;
  return (
    <div
      className={cn(
        'flex shrink-0 flex-wrap items-center gap-2 border-b border-border px-4 py-2',
        className,
      )}
    >
      {/* `basis-full` below `sm`: the controls to the right keep their natural
          width, so on a phone this side was handed whatever they left over —
          around 90px — and whatever a view puts here was squeezed into a
          column of single words. It gets its own row instead, and from `sm`
          up `basis-0` restores the single-row toolbar unchanged. */}
      <div className="flex min-w-0 flex-1 basis-full items-center gap-2 sm:basis-0">
        {children}
        {!hideRowCount && (
          <span className="text-xs tabular text-muted-foreground" aria-live="polite">
            {rowCount === totalCount
              ? `${totalCount} ${totalCount === 1 ? 'fila' : 'filas'}`
              : `${rowCount} de ${totalCount}`}
          </span>
        )}
      </div>

      {sort && sortLabel && onSortChange ? (
        <SortChip sort={sort} label={sortLabel} onClear={() => onSortChange(null)} />
      ) : (
        defaultSortLabel && <SortChip sort={null} label={defaultSortLabel} />
      )}

      {!hideDensity && (
        <TooltipProvider>
          <ToggleGroup
            type="single"
            value={density}
            onValueChange={(next: string) => {
              if (next === 'compact' || next === 'default') onDensityChange(next);
            }}
            aria-label="Densidad de las filas"
          >
            {(['default', 'compact'] as const).map((option) => {
              const Icon = option === 'compact' ? Rows3 : Rows2;
              return (
                <Tooltip key={option}>
                  <TooltipTrigger asChild>
                    <ToggleGroupItem value={option} aria-label={DENSITY_LABEL[option]}>
                      <Icon className="size-3.5" />
                    </ToggleGroupItem>
                  </TooltipTrigger>
                  <TooltipContent>{DENSITY_LABEL[option]}</TooltipContent>
                </Tooltip>
              );
            })}
          </ToggleGroup>
        </TooltipProvider>
      )}

      <DataTableColumnsMenu
        columns={columns}
        onToggle={onToggleColumn}
        onMove={onMoveColumn}
        onReset={onResetColumns}
      />
    </div>
  );
}
